import { Row, Col, Badge, Modal, Button, Alert, Tab, Tabs } from 'react-bootstrap'
import { ICustomColumn, IDealerChangedEvent, IGridLocalStorageNames, IPageProps, IRootState } from 'types'
import { DefaultButton, FormDropDownList, FormSwitch, LoadingBar, MessageAlert, PageHeader } from 'components'
import { MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react'
import { FormElement, Field, Form, FormClassComponent } from '@progress/kendo-react-form'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import { V1ApiIntegrationsClient } from 'core/http-common'
import {
    LS_SYNC_QUEUE_HISTORY_PAGE_SIZE,
    LS_SYNC_QUEUE_HISTORY_SEARCH,
    LS_SYNC_QUEUE_HISTORY_SELECTED_PAGE,
    LS_SYNC_QUEUE_HISTORY_SORT
} from 'core/consts'
import { DeletedStateEnum, F4fullSyncDetailDto, F4syncQueueResultDto, F4SyncQueueResultStatusEnum, QueueAllSyncRequestDto, SyncEntityTypeEnum, SyncOperationEnum } from 'generated/v1/v1IntegrationsApi'
import toast from 'core/toast'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GridCellProps, GridRowClickEvent } from '@progress/kendo-react-grid'
import ConfirmModal from 'components/Modals/ConfirmModal'
import { useDispatch, useSelector } from 'react-redux'
import DealerSelect from 'components/Dealers/DealerSelect'
import { getFromLocalStorage, saveToLocalStorage } from 'core/local-storage-helpers'
import { getErrors } from 'core/helpers'
import { AppDispatch } from 'state/store'

type SyncTypeOption = {
    text: string;
    value: SyncEntityTypeEnum | null;
}

type StatusOption = {
    text: string;
    value: F4SyncQueueResultStatusEnum | null;
}

type OperationOption = {
    text: string;
    value: SyncOperationEnum | null;
}

const LOCAL_STORAGE_KEYS = {
    DEALER: 'syncToolDealer'
}

// Columns for the Full Sync detail vehicle lists. Orphans show vehicle info;
// failure lists swap Year/Make/Model for the error Message.
const orphanVehicleColumns: ICustomColumn[] = [
    { field: 'inventoryId', title: 'ID', width: '90px' },
    { field: 'stock', title: 'Stock', width: '120px' },
    { field: 'vin', title: 'VIN', width: '180px' },
    { field: 'vehicleYear', title: 'Year', width: '90px' },
    { field: 'make', title: 'Make' },
    { field: 'model', title: 'Model' },
]

const failureVehicleColumns: ICustomColumn[] = [
    { field: 'inventoryId', title: 'ID', width: '90px' },
    { field: 'stock', title: 'Stock', width: '120px' },
    { field: 'vin', title: 'VIN', width: '180px' },
    { field: 'message', title: 'Message' },
]

const F4DataSyncQueuePage = ({ title }: IPageProps) => {

    const formRef = useRef<FormClassComponent>(null)
    const queueAllFormRef = useRef<FormClassComponent>(null)
    const gridRef = useRef<ISAGridRef>(null)
    const dispatch = useDispatch<AppDispatch>()

    const loading = useSelector((state: IRootState) => state.app.loading)

    const [queueAllModalErrors, setQueueAllModalErrors] = useState<string[]>([])

    const syncTypeData: Array<SyncTypeOption> = [
        { text: 'Inventory (Full Sync)', value: SyncEntityTypeEnum.InventoryFullSync },
        { text: 'Inventory', value: SyncEntityTypeEnum.Inventory },
        { text: 'Buyer', value: SyncEntityTypeEnum.Buyer },
        { text: 'Vendor', value: SyncEntityTypeEnum.Vendor },
        { text: 'Ad Source', value: SyncEntityTypeEnum.AdSource }
    ]

    const statusData: Array<StatusOption> = [
        { text: 'All', value: null },
        { text: 'Queued', value: F4SyncQueueResultStatusEnum.Queued },
        { text: 'Processing', value: F4SyncQueueResultStatusEnum.Processing },
        { text: 'Success', value: F4SyncQueueResultStatusEnum.Success },
        { text: 'Partial Success', value: F4SyncQueueResultStatusEnum.PartialSuccess },
        { text: 'Failed', value: F4SyncQueueResultStatusEnum.Failed },
        { text: 'No Change', value: F4SyncQueueResultStatusEnum.NoChange },
        { text: 'Duplicate', value: F4SyncQueueResultStatusEnum.Duplicate }
    ]

    const operationData: Array<OperationOption> = [
        { text: 'All', value: null },
        { text: 'Upsert', value: SyncOperationEnum.Upsert },
        { text: 'Delete', value: SyncOperationEnum.Delete }
    ]

    const [syncType, setSyncType] = useState<SyncTypeOption>(syncTypeData[0])
    const [status, setStatus] = useState<StatusOption>(statusData[0])
    const [operation, setOperation] = useState<OperationOption>(operationData[0])
    const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(getFromLocalStorage(LOCAL_STORAGE_KEYS.DEALER) as number | undefined)
    const [showQueueAllConfirm, setShowQueueAllConfirm] = useState<boolean>(false)
    const [showFullSyncConfirm, setShowFullSyncConfirm] = useState<boolean>(false)

    // Row-click application log lookup state. Only rows with an xRequestId
    // (i.e. F4 bulk-call failures) are clickable; the modal handles lookup,
    // confirm, and open-in-new-tab.
    const [logModalRequestId, setLogModalRequestId] = useState<string | undefined>(undefined)
    const [logModalLoading, setLogModalLoading] = useState<boolean>(false)
    const [logModalErrors, setLogModalErrors] = useState<string[]>([])
    const [logModalMatch, setLogModalMatch] = useState<{ indexName: string; id: string } | undefined>(undefined)

    // FullSync detail modal state — set when a FullSync row with structured detail
    // (counts + orphaned vehicles / failures) is clicked.
    const [detailModalItem, setDetailModalItem] = useState<F4syncQueueResultDto | undefined>(undefined)
    const [detailLoading, setDetailLoading] = useState<boolean>(false)
    const [detailErrors, setDetailErrors] = useState<string[]>([])
    const [detail, setDetail] = useState<F4fullSyncDetailDto | undefined>(undefined)
    const [detailTab, setDetailTab] = useState<string>('orphaned')

    const getBadgeVariant = (statusId: number | undefined) => {
        switch (statusId) {
            case 1: // Queued
                return 'secondary'
            case 2: // Processing
                return 'primary'
            case 3: // Success
                return 'success'
            case 4: // Failed
                return 'danger'
            case 5: // NoChange
                return 'info'
            case 6: // Duplicate
                return 'info'
            case 7: // PartialSuccess
                return 'warning'
            default:
                return 'secondary'
        }
    }

    const StatusCell = (props: GridCellProps) => {
        const item = props.dataItem as F4syncQueueResultDto
        const onBadgeClick = item.xRequestId
            ? (e: ReactMouseEvent) => {
                e.stopPropagation()
                openLogModal(item.xRequestId!)
            }
            : undefined

        return (
            <td className='text-center'>
                {item.statusName ?
                    <Badge
                        bg={getBadgeVariant(item.statusId)}
                        style={item.xRequestId ? { cursor: 'pointer' } : undefined}
                        title={item.xRequestId ? 'Open application log for this failure' : undefined}
                        onClick={onBadgeClick}
                    >
                        {item.statusName}
                        {item.xRequestId && (
                            <FontAwesomeIcon
                                icon={['fas', 'up-right-from-square']}
                                className='ms-1'
                            />
                        )}
                    </Badge>
                    : null
                }
            </td>
        )
    }

    const ForceResyncCell = (props: GridCellProps) => {
        const item = props.dataItem as F4syncQueueResultDto
        return <td className='text-center'>{item.forceResync ? 'Yes' : 'No'}</td>
    }

    // FullSync rows are dealer-level — Ref ID 0 is a marker, not an Inventory_ID.
    const RefIdCell = (props: GridCellProps) => {
        const item = props.dataItem as F4syncQueueResultDto
        return <td>{item.operationName === 'FullSync' ? 'All' : item.refId}</td>
    }

    const isFullSync = syncType.value === SyncEntityTypeEnum.InventoryFullSync

    // Full sync rows are dealer-level, so Ref ID ("All") and Force Resync carry no
    // useful per-row info — drop those two columns for that view only.
    const columns: ICustomColumn[] = [
        { field: 'f4SyncQueueResultId', title: 'ID', width: '100px' },
        { field: 'entityTypeName', title: 'Sync Type', width: '130px' },
        { field: 'operationName', title: 'Operation', width: '110px' },
        ...(isFullSync ? [] : [{ field: 'refId', title: 'Ref ID', width: '100px', cell: RefIdCell }]),
        { field: 'statusName', title: 'Status', width: '110px', cell: StatusCell },
        ...(isFullSync ? [] : [{ field: 'forceResync', title: 'Force Resync', width: '130px', cell: ForceResyncCell }]),
        { field: 'message', title: 'Message' },
        { field: 'server', title: 'Server', width: '150px' },
        { field: 'createdDate', title: 'Date', format: '{0:g}', filterType: 'date', width: '180px' },
    ]

    const gridLocalStorageNames: IGridLocalStorageNames =
    {
        pageSize: LS_SYNC_QUEUE_HISTORY_PAGE_SIZE,
        search: LS_SYNC_QUEUE_HISTORY_SEARCH,
        selectedPage: LS_SYNC_QUEUE_HISTORY_SELECTED_PAGE,
        sort: LS_SYNC_QUEUE_HISTORY_SORT
    }

    const getData = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        if (!selectedDealerId || !syncType.value) {
            return {
                data: [],
                total: 0
            }
        }

        const page = props.paging && props.paging.currentPage ? props.paging.currentPage : 1
        const take = props.paging ? props.paging.take : 10

        try {
            const result = syncType.value === SyncEntityTypeEnum.InventoryFullSync
                ? await V1ApiIntegrationsClient.getFullSyncResults(
                    selectedDealerId,
                    status.value,
                    props.search,
                    props.sortBy,
                    props.sortAscending,
                    page,
                    take,
                    true,
                )
                : syncType.value === SyncEntityTypeEnum.Inventory
                    ? await V1ApiIntegrationsClient.getInventorySyncQueueResults(
                        selectedDealerId,
                        status.value,
                        operation.value,
                        props.search,
                        props.sortBy,
                        props.sortAscending,
                        page,
                        take,
                        true,
                    )
                    : await V1ApiIntegrationsClient.getSyncQueueResults(
                        selectedDealerId,
                        syncType.value,
                        status.value,
                        operation.value,
                        props.search,
                        props.sortBy,
                        props.sortAscending,
                        page,
                        take,
                        true,
                    )

            return {
                data: result.data,
                total: result.totalItems ? result.totalItems : 0
            }
        }
        catch (e) {
            toast.responseErrors(e)
        }
        return {
            data: [],
            total: 0
        }
    }

    const handleDealerClear = async () => {
        setSelectedDealerId(undefined)
        saveToLocalStorage(LOCAL_STORAGE_KEYS.DEALER, undefined)
        gridRef.current?.resetAndFetch()
    }

    const handleDealerChange = async (e: IDealerChangedEvent) => {
        setSelectedDealerId(e.dealerId)
        saveToLocalStorage(LOCAL_STORAGE_KEYS.DEALER, e.dealerId)
        gridRef.current?.resetAndFetch()
    }

    const handleSyncTypeChange = () => {
        const value = formRef.current?.valueGetter('syncType') as SyncTypeOption | undefined
        if (value) {
            setSyncType(value)
            // Full sync has no per-record operation -- the dropdown is hidden, so force
            // its filter back to All to avoid carrying a stale Upsert/Delete selection.
            if (value.value === SyncEntityTypeEnum.InventoryFullSync) {
                setOperation(operationData[0])
                formRef.current?.valueSetter('operation', operationData[0])
            }
        }
        gridRef.current?.resetAndFetch()
    }

    const handleStatusChange = () => {
        const value = formRef.current?.valueGetter('status')
        if (value) {
            setStatus(value as StatusOption)
        }
        gridRef.current?.resetAndFetch()
    }

    const handleOperationChange = () => {
        const value = formRef.current?.valueGetter('operation')
        if (value) {
            setOperation(value as OperationOption)
        }
        gridRef.current?.resetAndFetch()
    }

    const openLogModal = (xRequestId: string) => {
        setLogModalRequestId(xRequestId)
        setLogModalMatch(undefined)
        setLogModalErrors([])
    }

    const handleRowClick = (e: GridRowClickEvent) => {
        const item = e.dataItem as F4syncQueueResultDto
        // FullSync detail takes precedence — it links to the log modal itself
        // when the run also produced an application log.
        if (item.hasFullSyncDetail) {
            setDetailModalItem(item)
            return
        }
        if (item.xRequestId) openLogModal(item.xRequestId)
    }

    const closeLogModal = () => {
        setLogModalRequestId(undefined)
        setLogModalLoading(false)
        setLogModalErrors([])
        setLogModalMatch(undefined)
    }

    // Auto-search as soon as the modal opens — the user already chose to open
    // the log when they clicked the row, no reason to make them click again.
    useEffect(() => {
        if (!logModalRequestId) return
        let cancelled = false
        const lookup = async () => {
            setLogModalLoading(true)
            try {
                const result = await V1ApiIntegrationsClient.getApplicationLogs(
                    1, 1, true, '@timestamp', false,
                    undefined, undefined,
                    logModalRequestId,
                    undefined, undefined, undefined
                )
                if (cancelled) return
                const hit = result.data?.[0]
                if (hit?.indexName && hit?.id) {
                    setLogModalMatch({ indexName: hit.indexName, id: hit.id })
                } else {
                    setLogModalErrors(['No application log found for this request id.'])
                }
            } catch (e) {
                if (!cancelled) setLogModalErrors(getErrors(e))
            } finally {
                if (!cancelled) setLogModalLoading(false)
            }
        }
        lookup()
        return () => { cancelled = true }
    }, [logModalRequestId])

    // Fetch the structured FullSync detail (counts + orphaned / failure lists) when a
    // FullSync row is opened.
    useEffect(() => {
        const queueId = detailModalItem?.f4syncQueueResultId
        if (!selectedDealerId || !queueId) return
        let cancelled = false
        const fetchDetail = async () => {
            setDetailLoading(true)
            setDetail(undefined)
            setDetailErrors([])
            try {
                const result = await V1ApiIntegrationsClient.getFullSyncDetail(selectedDealerId, queueId)
                if (!cancelled) {
                    setDetail(result)
                    // Land on the first tab that actually has rows.
                    setDetailTab(
                        result.orphanedVehicles?.length ? 'orphaned'
                            : result.upsertFailures?.length ? 'upsert'
                                : 'delete'
                    )
                }
            } catch (e) {
                if (!cancelled) setDetailErrors(getErrors(e))
            } finally {
                if (!cancelled) setDetailLoading(false)
            }
        }
        fetchDetail()
        return () => { cancelled = true }
    }, [selectedDealerId, detailModalItem])

    const closeDetailModal = () => {
        setDetailModalItem(undefined)
        setDetail(undefined)
        setDetailErrors([])
        setDetailLoading(false)
    }

    const openMatchedLog = () => {
        if (!logModalMatch) return
        const { indexName, id } = logModalMatch
        closeLogModal()
        window.open(`/logs/application-logs/${indexName}/${id}`, '_blank', 'noopener,noreferrer')
    }

    // Full Sync is offered from both the per-vehicle Inventory view and the dedicated
    // Full Sync view; queuing it always targets the Full Sync pipeline.
    const showFullSyncButton = syncType.value === SyncEntityTypeEnum.Inventory || isFullSync
    // Buyer / Vendor / AdSource use the per-record Queue All. Regular Inventory is
    // otherwise view-only (triggers enqueue per-vehicle automatically).
    const canQueueAll = syncType.value != null
        && syncType.value !== SyncEntityTypeEnum.Inventory
        && syncType.value !== SyncEntityTypeEnum.InventoryFullSync

    // Switch the page to the Full Sync view (dropdown + grid) so the user lands on the
    // run they just queued.
    const switchToFullSyncView = () => {
        const fullSyncOption = syncTypeData.find(o => o.value === SyncEntityTypeEnum.InventoryFullSync)
        if (!fullSyncOption) return
        formRef.current?.valueSetter('syncType', fullSyncOption)
        setSyncType(fullSyncOption)
        setStatus(statusData[0])
        setOperation(operationData[0])
        formRef.current?.valueSetter('status', statusData[0])
        formRef.current?.valueSetter('operation', operationData[0])
        gridRef.current?.resetAndFetch()
    }

    const handleQueueAll = async () => {
        if (!selectedDealerId || !syncType.value) return

        const forceResync = queueAllFormRef.current?.valueGetter('forceResync') === true

        try {
            await dispatch(startLoadingAction())

            // DeletedState is unused by these queue paths -- kept for wire compatibility.
            const response = await V1ApiIntegrationsClient.queueAllByEntityType(
                selectedDealerId,
                new QueueAllSyncRequestDto({ entityType: syncType.value, forceResync, deletedState: DeletedStateEnum.Active })
            )

            toast.success(`Queued ${response.id} ${syncType.text} record(s) for sync.`)
            setQueueAllModalErrors([])
            setShowQueueAllConfirm(false)
            setTimeout(() => gridRef.current?.fetch(), 0)
        } catch (e) {
            setQueueAllModalErrors(getErrors(e))
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    const handleFullSync = async () => {
        if (!selectedDealerId) return

        try {
            await dispatch(startLoadingAction())

            // Always targets the Full Sync pipeline regardless of which inventory view the
            // button was clicked from. Dealer-level, so force-resync doesn't apply.
            const response = await V1ApiIntegrationsClient.queueAllByEntityType(
                selectedDealerId,
                new QueueAllSyncRequestDto({ entityType: SyncEntityTypeEnum.InventoryFullSync, forceResync: false, deletedState: DeletedStateEnum.Active })
            )

            if (response.id === 0) {
                toast.info('A full inventory sync is already pending for this dealer.')
            } else {
                toast.success('Full inventory sync queued for this dealer.')
            }
            setQueueAllModalErrors([])
            setShowFullSyncConfirm(false)
            switchToFullSyncView()
        } catch (e) {
            setQueueAllModalErrors(getErrors(e))
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    return (
        <div id='SyncToolPage'>
            <PageHeader title={`${title} (Last 30 Days)`}>
                {showFullSyncButton && (
                    <DefaultButton
                        className='me-2'
                        title="Sync this dealer's sale-connected vehicles with Frazer"
                        onClick={() => {
                            setQueueAllModalErrors([])
                            setShowFullSyncConfirm(true)
                        }}
                        disabled={!selectedDealerId || loading}
                        variant='primary'>
                        <FontAwesomeIcon icon={['fas', 'layer-group']} /> Full Sync
                    </DefaultButton>
                )}
                {canQueueAll && (
                    <DefaultButton
                        className='me-2'
                        title={`Queue all ${syncType.text} records for sync`}
                        onClick={() => {
                            setQueueAllModalErrors([])
                            setShowQueueAllConfirm(true)
                        }}
                        disabled={!selectedDealerId || loading}
                        variant='primary'>
                        <FontAwesomeIcon icon={['fas', 'layer-group']} /> Queue All
                    </DefaultButton>
                )}
                <DefaultButton
                    title='Refresh sync queue results'
                    onClick={() => { gridRef.current?.fetch() }}
                    disabled={!selectedDealerId || loading}
                    spinnerVariant='default'
                    variant='default'>
                    <FontAwesomeIcon icon={['fas', 'sync']} /> Refresh
                </DefaultButton>
            </PageHeader>

            <Form
                ref={formRef}
                initialValues={{
                    syncType: syncTypeData[0],
                    status: statusData[0],
                    operation: operationData[0],
                    dealer: null
                }}
                ignoreModified={true}
                render={() => (
                    <FormElement>
                        <Row className='mb-3'>
                            <Col md={3}>
                                <Field
                                    label='Sync Type:'
                                    name='syncType'
                                    textField='text'
                                    dataItemKey='value'
                                    component={FormDropDownList}
                                    data={syncTypeData}
                                    onChange={handleSyncTypeChange}
                                />
                            </Col>
                            <Col md={2}>
                                <Field
                                    label='Status:'
                                    name='status'
                                    textField='text'
                                    dataItemKey='value'
                                    component={FormDropDownList}
                                    data={statusData}
                                    onChange={handleStatusChange}
                                />
                            </Col>
                            {/* Operation (Upsert/Delete) doesn't apply to a dealer-level full sync. */}
                            {!isFullSync && (
                                <Col md={2}>
                                    <Field
                                        label='Operation:'
                                        name='operation'
                                        textField='text'
                                        dataItemKey='value'
                                        component={FormDropDownList}
                                        data={operationData}
                                        onChange={handleOperationChange}
                                    />
                                </Col>
                            )}
                            <Col md={isFullSync ? 7 : 5} className='mb-3 mb-md-0'>
                                <Field
                                    name='dealer'
                                    label='Dealer:'
                                    component={DealerSelect}
                                    dealerId={selectedDealerId ?? 0}
                                    disabled={loading}
                                    onDealerChanged={handleDealerChange}
                                    onDealerCleared={handleDealerClear}
                                    required={false}
                                />
                            </Col>
                        </Row>
                    </FormElement>
                )}
            />

            <hr />

            {!selectedDealerId
                ? <MessageAlert
                    alert={{
                        variant: 'info', messages: ['Please select a dealer.']
                    }}
                />
                : <SAGrid
                    ref={gridRef}
                    columns={columns}
                    defaultSort={[{ field: 'createdDate', dir: 'desc' }]}
                    getData={getData}
                    localStorageNames={gridLocalStorageNames}
                    identifierPropertyName='f4SyncQueueResultId'
                    onRowClick={handleRowClick}
                    features={
                        {
                            pageable: true,
                            reorderColumns: true,
                            sortable: true,
                            simpleSearch: true
                        }
                    }
                />
            }

            <ConfirmModal
                show={showFullSyncConfirm}
                title='Full Inventory Sync'
                confirmButtonTitle='Queue Full Sync'
                loading={loading}
                disabled={loading}
                errors={queueAllModalErrors}
                onCancel={() => {
                    setQueueAllModalErrors([])
                    setShowFullSyncConfirm(false)
                }}
                onSave={handleFullSync}
                content={
                    <p>
                        Re-syncs this dealer&apos;s entire inventory with Frazer.
                        We push the current DCS data for all of this dealer&apos;s vehicles,
                        and Frazer updates its records to match &mdash; adding new vehicles, updating existing ones,
                        and removing any it&apos;s holding that no longer exist in DCS.
                    </p>
                }
            />

            <ConfirmModal
                show={showQueueAllConfirm}
                title='Queue All for Sync'
                confirmButtonTitle='Queue All'
                loading={loading}
                disabled={loading}
                errors={queueAllModalErrors}
                onCancel={() => {
                    setQueueAllModalErrors([])
                    setShowQueueAllConfirm(false)
                }}
                onSave={handleQueueAll}
                content={
                    <>
                        <p>Are you sure you want to queue all <strong>{syncType.text}</strong> records for this dealer?</p>
                        <Form
                            ref={queueAllFormRef}
                            initialValues={{
                                forceResync: false
                            }}
                            ignoreModified={true}
                            render={() => (
                                <FormElement>
                                    <Field
                                        name='forceResync'
                                        label='Force Resync:'
                                        component={FormSwitch}
                                        onLabel='Yes'
                                        offLabel='No'
                                    />
                                </FormElement>
                            )}
                        />
                    </>
                }
            />

            {/* Full Sync detail: read-only counts + orphaned / failure lists for a completed run. */}
            <Modal show={!!detailModalItem} onHide={closeDetailModal} size='lg'>
                <Modal.Header closeButton={!detailLoading}>
                    <Modal.Title>Full Sync Detail</Modal.Title>
                </Modal.Header>
                <LoadingBar loading={detailLoading} />
                <Modal.Body>
                    <Alert variant='danger' show={detailErrors.length > 0}>
                        <ul>
                            {detailErrors.map((value, index) => <li key={index}>{value}</li>)}
                        </ul>
                    </Alert>

                    {detail && (
                        <>
                            <p>
                                <Badge bg='success' className='me-2'>{detail.upsertedCount} upserted</Badge>
                                <Badge bg='danger' className='me-2'>{detail.deletedCount} deleted</Badge>
                                <Badge bg='warning' text='dark'>{detail.orphanedVehicles?.length ?? 0} orphaned</Badge>
                            </p>

                            {detail.orphanedVehicles?.length || detail.upsertFailures?.length || detail.deleteFailures?.length ? (
                                <Tabs activeKey={detailTab} onSelect={(k) => setDetailTab(k!)} id='full-sync-detail-tabs' className='mt-2'>
                                    {!!detail.orphanedVehicles?.length && (
                                        <Tab eventKey='orphaned' title={`Orphaned Vehicles (${detail.orphanedVehicles.length})`}>
                                            <p className='text-muted mt-2 mb-2'>
                                                <small>
                                                    Orphaned vehicles were kept by Frazer because they are attached to a sale,
                                                    but no longer exist in DCS inventory. They may need manual investigation.
                                                </small>
                                            </p>
                                            <SAGrid
                                                columns={orphanVehicleColumns}
                                                data={detail.orphanedVehicles}
                                                defaultSort={[{ field: 'inventoryId', dir: 'asc' }]}
                                                identifierPropertyName='inventoryId'
                                                features={{ pageable: detail.orphanedVehicles.length > 10, sortable: true }}
                                            />
                                        </Tab>
                                    )}
                                    {!!detail.upsertFailures?.length && (
                                        <Tab eventKey='upsert' title={`Upsert Failures (${detail.upsertFailures.length})`}>
                                            <div className='mt-2'>
                                                <SAGrid
                                                    columns={failureVehicleColumns}
                                                    data={detail.upsertFailures}
                                                    defaultSort={[{ field: 'inventoryId', dir: 'asc' }]}
                                                    identifierPropertyName='inventoryId'
                                                    features={{ pageable: detail.upsertFailures.length > 10, sortable: true }}
                                                />
                                            </div>
                                        </Tab>
                                    )}
                                    {!!detail.deleteFailures?.length && (
                                        <Tab eventKey='delete' title={`Delete Failures (${detail.deleteFailures.length})`}>
                                            <div className='mt-2'>
                                                <SAGrid
                                                    columns={failureVehicleColumns}
                                                    data={detail.deleteFailures}
                                                    defaultSort={[{ field: 'inventoryId', dir: 'asc' }]}
                                                    identifierPropertyName='inventoryId'
                                                    features={{ pageable: detail.deleteFailures.length > 10, sortable: true }}
                                                />
                                            </div>
                                        </Tab>
                                    )}
                                </Tabs>
                            ) : (
                                <p className='text-muted mb-0'>No orphaned vehicles or failures — everything synced cleanly.</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {detailModalItem?.xRequestId && (
                        <div className='me-auto'>
                            <Button
                                variant='default'
                                onClick={() => {
                                    const xRequestId = detailModalItem.xRequestId!
                                    closeDetailModal()
                                    openLogModal(xRequestId)
                                }}
                                disabled={detailLoading}>
                                <FontAwesomeIcon icon={['fas', 'up-right-from-square']} /> View Application Log
                            </Button>
                        </div>
                    )}
                    <Button variant='default' onClick={closeDetailModal} disabled={detailLoading}>Close</Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                show={!!logModalRequestId}
                title='View Application Log'
                loading={logModalLoading}
                disabled={logModalLoading || !logModalMatch}
                errors={logModalErrors}
                onCancel={closeLogModal}
                onSave={openMatchedLog}
                confirmButtonTitle='Open Log'
                content={
                    logModalLoading ? (
                        <>
                            <p>Looking up application log for request <code>{logModalRequestId}</code>&hellip;</p>
                            <p className='text-muted'>
                                <small>It can take a minute for new logs to be indexed and become searchable.</small>
                            </p>
                        </>
                    ) : logModalMatch ? (
                        <p>Found a matching application log. Click <strong>Open Log</strong> to view it.</p>
                    ) : (
                        <p className='text-muted'>
                            <small>If the failure just happened, give it a minute &mdash; logs can take a moment to be indexed.</small>
                        </p>
                    )
                }
            />
        </div>
    )
}
export default F4DataSyncQueuePage
